import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ClipboardList, Copy, Check, Share2, ArrowLeft, Activity, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DistributorFormsPage = () => {
  const navigate = useNavigate();
  const [registrationToken, setRegistrationToken] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/distributor/registration-token`).catch(() => ({ data: { token: '' } })),
      axios.get(`${API}/health-templates`).catch(() => ({ data: [] })),
    ]).then(([tokenRes, tplRes]) => {
      setRegistrationToken(tokenRes.data.token || '');
      setTemplates(tplRes.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const getFormLink = (formSlug) => `${window.location.origin}/health-form/${registrationToken}/${formSlug}`;

  const copyLink = (formSlug) => {
    navigator.clipboard.writeText(getFormLink(formSlug));
    setCopiedId(formSlug);
    toast.success('Link del formulario copiado');
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#467322]" size={32} /></div>;

  return (
    <div className="max-w-2xl mx-auto space-y-5 animate-fade-in" data-testid="distributor-forms-page">
      <button onClick={() => navigate('/distributor')} className="flex items-center gap-2 text-sm text-[#467322] font-semibold hover:text-[#1A2E10] transition-colors" data-testid="back-to-profile">
        <ArrowLeft size={16} /> Inicio
      </button>

      <div className="pt-1">
        <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Formularios de Salud
        </h1>
        <p className="text-sm text-gray-400 mt-1 font-medium">
          {templates.length} formulario{templates.length !== 1 ? 's' : ''} disponible{templates.length !== 1 ? 's' : ''} para compartir
        </p>
      </div>

      <div className="space-y-4">
        {templates.map((tpl) => {
          const isCopied = copiedId === tpl.slug;
          return (
            <div key={tpl.id} className="bg-white rounded-2xl border border-gray-100/50 overflow-hidden shadow-sm" data-testid={`form-card-${tpl.slug}`}>
              <div className="h-1.5" style={{ background: `linear-gradient(90deg, ${tpl.color}, ${tpl.color}99)` }} />
              <div className="p-5">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 border border-gray-100/50"
                    style={{ background: `linear-gradient(135deg, ${tpl.color}10, ${tpl.color}20)` }}>
                    <Activity size={28} style={{ color: tpl.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {tpl.name}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">{tpl.description}</p>
                    <div className="flex gap-2 mt-2">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                        {tpl.total_questions} preguntas
                      </span>
                      {tpl.blocks?.length > 0 && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                          {tpl.blocks.length} bloques
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {registrationToken && (
                  <div className="bg-gray-50/80 rounded-xl p-4 border border-gray-100/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Share2 size={14} style={{ color: tpl.color }} />
                      <span className="text-sm font-bold text-gray-700">Compartir con clientes</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      Los clientes que accedan a este link podran registrarse y completar el formulario directamente.
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-white rounded-xl px-3 py-2.5 text-xs text-gray-600 font-mono truncate border border-gray-100" data-testid={`form-link-${tpl.slug}`}>
                        {getFormLink(tpl.slug)}
                      </div>
                      <button
                        onClick={() => copyLink(tpl.slug)}
                        className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold transition-all duration-300 flex-shrink-0 ${
                          isCopied ? 'bg-green-500 text-white' : 'text-white hover:shadow-lg'
                        }`}
                        style={!isCopied ? { background: `linear-gradient(135deg, ${tpl.color}, ${tpl.color}dd)` } : {}}
                        data-testid={`copy-form-link-${tpl.slug}`}
                      >
                        {isCopied ? <Check size={14} /> : <Copy size={14} />}
                        {isCopied ? 'Copiado' : 'Copiar'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {templates.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <ClipboardList size={40} className="text-gray-200 mx-auto mb-3" />
          <p className="font-bold text-gray-500">No hay formularios disponibles</p>
        </div>
      )}

      <div className="bg-gradient-to-br from-violet-50/80 to-purple-50/80 rounded-2xl p-5 border border-violet-100/30">
        <div className="flex items-start gap-3">
          <ClipboardList size={20} className="text-violet-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-gray-800">Como funciona?</p>
            <ol className="text-xs text-gray-600 mt-2 space-y-1.5 list-decimal list-inside leading-relaxed">
              <li>Copia el link del formulario que deseas compartir</li>
              <li>Envialo a tu cliente por WhatsApp, correo o cualquier medio</li>
              <li>El cliente vera una introduccion y podra registrarse o iniciar sesion</li>
              <li>Al completar el formulario, los resultados quedaran vinculados a tu perfil</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DistributorFormsPage;
